/**
 * The beats of a performance: when each one fell, in seconds.
 *
 * A player's beat is not a clock's. It slows into a cadence, waits on a held note and picks
 * up again, so no one period laid over the whole performance stays on it for long. The beat
 * is therefore followed, not fitted: every attack is tried as a beat, with every length of
 * beat the range allows, and the best run of beats through the performance is the one whose
 * attacks weigh most for what it costs -- a beat nothing is struck on, an attack left between
 * two beats, and a beat that is not the length of the one before it. The last is what lets
 * the tempo move: it costs a little to change at each beat and nothing to have changed, so a
 * ritardando is followed beat by beat and a beat twice as long as the last one is not taken.
 *
 * Nothing leans the beat to a tempo. An attack left between beats costs more than half of
 * what a beat on it earns, so the beats settle on the fastest level most attacks fall on
 * that the range allows, and what is struck between them falls on the halves, thirds and
 * quarters a written beat is cut in. Which written value that level is -- a quarter, an
 * eighth -- is not something a performance says, and is read as a quarter
 * ({@link unperformed}).
 *
 * Followed that freely, the beats change level where the playing thins or thickens: every
 * other beat for a few bars, then every one again. A piece has one beat, so the performance
 * is followed twice. The first time finds the beat's usual length; the second is held to it,
 * a beat costing more the further its length is from that one, which leaves room for a
 * ritardando and none for a beat of half the length.
 *
 * This is dynamic programming over the attacks (after Ellis 2007, with the beat's length a
 * part of the state as in Cemgil's and Raphael's tempo trackers). The constants below were
 * set on recordings aligned to their scores and checked on scores performed by rule
 * (docs/02, "MIDI boundary", has what each reads).
 */

import type { Attack } from "./onsets";

/** The shortest and the longest beat followed, in seconds: 240 and under 40 a minute. */
const FASTEST = 0.25;
const SLOWEST = 1.6;
/** The lengths of beat tried, each this much longer than the last. */
const STEP = 1.04;
/** How much longer or shorter than the beat before it a beat may be. */
const SWING = 1.6;
/** What a change of tempo costs, for each beat, on the square of the log of the change. */
const TIGHT = 3;
/** What a beat with nothing struck on it costs, against the weight of an average attack. */
const SILENT = 0.3;
/** What an attack between two beats costs, of its own weight, on a written split of the beat. */
const BETWEEN = 0.5;
/** What it costs more, of its own weight, for each beat's length it sits off a written split. */
const ASTRAY = 4;
/** The most beats between two attacks taken as beats, where something is struck between them. */
const MOST = 4;
/** How far under the shortest beat the second following may go, once the usual beat is known. */
const UNDER = 1.3;
/** How many times longer or shorter than the usual beat a beat of the second following may be. */
const BAND = 2;
/** What a beat costs in the second following, on the square of the log of how far it is from the usual one. */
const HELD = 6;
/** The attacks at either end the beats may start after or stop before. */
const EDGE = 4;

/** Where a beat is cut when it is written: halves, thirds and quarters. */
const SPLITS = [0, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4, 1];

function astray(fraction: number): number {
  let least = 1;
  for (const split of SPLITS) least = Math.min(least, Math.abs(fraction - split));
  return least;
}

/** One run of beats ending on an attack with a beat of one length: its worth, and what it follows. */
interface Runs {
  readonly worth: Float64Array;
  readonly attack: Int32Array;
  readonly length: Int32Array;
  readonly beats: Int32Array;
}

/** The lengths of beat one following allows, and the usual one it is held to, if any. */
interface Range {
  readonly shortest: number;
  readonly longest: number;
  readonly usual: number | null;
}

/** The best run of beats through the attacks, earliest first, covering every attack. */
function follow(times: readonly number[], weights: readonly number[], range: Range): number[] {
  const count = times.length;
  const lengths: number[] = [];
  for (let length = range.shortest; length <= range.longest * 1.0001; length *= STEP) {
    lengths.push(length);
  }
  const kinds = lengths.length;
  const kindOf = (length: number): number =>
    Math.round(Math.log(length / range.shortest) / Math.log(STEP));
  const swing = Math.ceil(Math.log(SWING) / Math.log(STEP));
  const usual = range.usual;
  const held = lengths.map((length) => (usual === null ? 0 : HELD * Math.log(length / usual) ** 2));

  const runs: Runs[] = times.map(() => ({
    worth: new Float64Array(kinds).fill(-Infinity),
    attack: new Int32Array(kinds).fill(-1),
    length: new Int32Array(kinds).fill(-1),
    beats: new Int32Array(kinds).fill(1),
  }));
  // The first beat is one of the first attacks, those before it being left out and paid for.
  let before = 0;
  for (let first = 0; first < Math.min(count, EDGE); first += 1) {
    const run = runs[first] as Runs;
    for (let kind = 0; kind < kinds; kind += 1) {
      run.worth[kind] = (weights[first] as number) - before - (held[kind] as number);
    }
    before += weights[first] as number;
  }

  for (let here = 1; here < count; here += 1) {
    const run = runs[here] as Runs;
    for (let last = here - 1; last >= 0; last -= 1) {
      const gap = (times[here] as number) - (times[last] as number);
      const next = last === here - 1;
      if (gap > MOST * range.longest && !next) break;
      const from = runs[last] as Runs;
      // Across a silence the beats go on, however many it takes: nothing is struck to count them by.
      const fewest = Math.max(1, Math.ceil(gap / (range.longest * 1.02)));
      const most = next ? Math.max(MOST, Math.floor(gap / range.shortest)) : MOST;
      for (let beats = fewest; beats <= most; beats += 1) {
        const length = gap / beats;
        const kind = kindOf(length);
        if (kind < 0 || kind >= kinds) continue;
        let cost = SILENT * (beats - 1) + beats * (held[kind] as number);
        for (let between = last + 1; between < here; between += 1) {
          const place = ((times[between] as number) - (times[last] as number)) / length;
          cost +=
            (weights[between] as number) * (BETWEEN + ASTRAY * astray(place - Math.floor(place)));
        }
        const earned = (weights[here] as number) - cost;
        const lowest = Math.max(0, kind - swing);
        const highest = Math.min(kinds - 1, kind + swing);
        for (let was = lowest; was <= highest; was += 1) {
          const had = from.worth[was] as number;
          if (had === -Infinity) continue;
          const change = Math.log(length / (lengths[was] as number));
          const worth = had + earned - TIGHT * change * change;
          if (worth > (run.worth[kind] as number)) {
            run.worth[kind] = worth;
            run.attack[kind] = last;
            run.length[kind] = was;
            run.beats[kind] = beats;
          }
        }
      }
    }
  }

  // The last beat is one of the last attacks, those after it being left out and paid for;
  // where none of them can be reached, as a chord rolled after a long silence, the last that can.
  let best = -Infinity;
  let at = count - 1;
  let kind = 0;
  let after = 0;
  for (let last = count - 1; last >= 0 && (last >= count - EDGE || best === -Infinity); last -= 1) {
    const run = runs[last] as Runs;
    for (let one = 0; one < kinds; one += 1) {
      const worth = (run.worth[one] as number) - after;
      if (worth > best) {
        best = worth;
        at = last;
        kind = one;
      }
    }
    after += weights[last] as number;
  }

  const found: number[] = [];
  for (;;) {
    const run = runs[at] as Runs;
    const time = times[at] as number;
    found.unshift(time);
    const last = run.attack[kind] as number;
    if (last < 0) break;
    const beats = run.beats[kind] as number;
    const start = times[last] as number;
    // The beats nothing was struck on fall evenly between the two attacks.
    for (let silent = beats - 1; silent >= 1; silent -= 1) {
      found.unshift(start + ((time - start) * silent) / beats);
    }
    kind = run.length[kind] as number;
    at = last;
  }
  if (found.length < 2) return [];

  // Straight on before the first beat and after the last, to cover every attack.
  const opening = (found[1] as number) - (found[0] as number);
  while ((found[0] as number) > (times[0] as number) + 1e-9) {
    found.unshift((found[0] as number) - opening);
  }
  const closing = (found[found.length - 1] as number) - (found[found.length - 2] as number);
  while ((found[found.length - 1] as number) < (times[count - 1] as number) - 1e-9) {
    found.push((found[found.length - 1] as number) + closing);
  }
  return found;
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((first, second) => first - second);
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

/** The beat times of a performance, earliest first, covering every attack. */
export function beatsOf(attacks: readonly Attack[]): number[] {
  const count = attacks.length;
  if (count < 2) return [];
  const mean = attacks.reduce((sum, attack) => sum + attack.weight, 0) / count;
  const weights = attacks.map((attack) => attack.weight / mean);
  const times = attacks.map((attack) => attack.time);

  const free = follow(times, weights, { shortest: FASTEST, longest: SLOWEST, usual: null });
  if (free.length < 2) return free;
  const usual = median(free.slice(1).map((beat, index) => beat - (free[index] as number)));
  const kept = follow(times, weights, {
    shortest: Math.max(FASTEST / UNDER, usual / BAND),
    longest: usual * BAND,
    usual,
  });
  return kept.length < 2 ? free : kept;
}
